import { LoadingButton } from '@mui/lab';
import { Tab, Tabs, TextField, Typography } from '@mui/material';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import React, { FormEvent, useMemo, useState } from 'react';
import {
  GadgetIconsEnum,
  GadgetsIcon,
  getIcon,
} from '../../../../components/IconSelect/icons';
import { IconSelect } from '../../../../components/IconSelect/IconSelect';
import { GadgetEdit, GadgetSeoEdit } from '../../GadgetsPage.type';
import styles from './GadgetModal.module.css';
import { ACTIVE_TAB } from '../../../BlogPage/components/PostForm/consts';
import { chooseLangAccordingToTab } from '../../../ServicesPage/components/useServiceInfoForm';
import { GadgetSeoDto } from '../../../../types/gadgets.types';
import WarningIcon from '@mui/icons-material/Warning';
import { FaqByLang } from '../../../ServicesPage/modals/ServiceHub/utils/types';
import { JsonEditor } from 'json-edit-react';
import { chooseFaqKeyAccordingToTab, isServiceHubFaqArray } from '../../../ServicesPage/modals/ServiceHub/utils/utils';
import { UploadImage } from '../../../../components/UploadImage/UploadImage';
interface GadgetFormProps {
  onSubmit: (gadget: GadgetEdit, seo: GadgetSeoEdit) => void;
  onCancel: () => void;
  defaultValue?: GadgetEdit;
  loading: boolean;
  seoDefaultValue?: GadgetSeoDto | null;
}

export const GadgetForm = ({
  onSubmit,
  defaultValue,
  onCancel,
  seoDefaultValue,
  loading,
}: GadgetFormProps) => {
  const [name, setName] = useState(defaultValue?.name || '');
  const [slug, setSlug] = useState(defaultValue?.slug || '');
  const [iconValue, setIconValue] = useState(
    getIcon(defaultValue?.icon as GadgetIconsEnum)?.value ||
      GadgetsIcon[0].value,
  );

  const [page, setPage] = useState(seoDefaultValue || {
    titleEn: '',
    descriptionEn: '',
    h1En: '',

    titleRu: '',
    descriptionRu: '',
    h1Ru: '',

    titlePl: '',
    descriptionPl: '',
    h1Pl: '',

    titleUk: '',
    descriptionUk: '',
    h1Uk: '',

    modelTitleEn: '',
    modelDescriptionEn: '',
    modelH1En: '',

    modelTitleRu: '',
    modelDescriptionRu: '',
    modelH1Ru: '',

    modelTitlePl: '',
    modelDescriptionPl: '',
    modelH1Pl: '',

    modelTitleUk: '',
    modelDescriptionUk: '',
    modelH1Uk: '',

    subtitleUk: '',
    subtitleRu: '',
    subtitlePl: '',
    subtitleEn: '',

  });

  const [activeTabIndex, setActiveTabIndex] = useState(ACTIVE_TAB.POLISH);

  const title = `title${chooseLangAccordingToTab(activeTabIndex)}` as 'titleEn' | 'titleRu' | 'titleUk' | 'titlePl';
  const description = `description${chooseLangAccordingToTab(activeTabIndex)}` as 'descriptionEn' | 'descriptionRu' | 'descriptionUk' | 'descriptionPl';
  const h1 = `h1${chooseLangAccordingToTab(activeTabIndex)}` as 'h1En' | 'h1Ru' | 'h1Uk' | 'h1Pl';

  const modelH1 = `modelH1${chooseLangAccordingToTab(activeTabIndex)}` as 'modelH1En' | 'modelH1Ru' | 'modelH1Uk' | 'modelH1Pl';
  const modelDescription = `modelDescription${chooseLangAccordingToTab(activeTabIndex)}` as 'modelDescriptionEn' | 'modelDescriptionRu' | 'modelDescriptionUk' | 'modelDescriptionPl';
  const modelTitle = `modelTitle${chooseLangAccordingToTab(activeTabIndex)}` as 'modelTitleEn' | 'modelTitleRu' | 'modelTitleUk' | 'modelTitlePl';
  const subtitle = `subtitle${chooseLangAccordingToTab(activeTabIndex)}` as 'subtitleEn' | 'subtitleRu' | 'subtitleUk' | 'subtitlePl';
  const [serviceFaq, setServiceFaq] = useState<FaqByLang>(() => ({
    faqEn: defaultValue?.faqEn ?? [],
    faqRu: defaultValue?.faqRu ?? [],
    faqPl: defaultValue?.faqPl ?? [],
    faqUk: defaultValue?.faqUk ?? [],
  }));

  const [previewUrl, setPreviewUrl] = useState<string | null>(
    defaultValue?.fileInfo?.fileName
      ? `${process.env.REACT_APP_API_URL_STATIC}/gadget/${defaultValue.fileInfo.fileName}`
      : null,
  );
  const [fileName, setFileName] = useState<string>('');
  const [ fileInfo, setFileInfo] = useState<any>();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setFileName(file.name);
      setFileInfo(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const faqKey = chooseFaqKeyAccordingToTab(activeTabIndex);
  const onSubmitHdl = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isSubmitBtnDisabled) return;

    onSubmit({ name, icon: iconValue, slug, ...serviceFaq, fileInfo }, page);
  };

  const isSubmitBtnDisabled = useMemo(() => {
    return !name.trim() || !slug.trim();
  }, [name, slug]);

  const onTabClick = (_: React.SyntheticEvent, nextIndex: number) => {
    setActiveTabIndex(nextIndex);
  };
  return (
    <form className={styles.form} onSubmit={onSubmitHdl} noValidate>
      <TextField
        name="name"
        label="Gadget"
        value={name}
        variant="standard"
        required
        onChange={(e) => setName(e.target.value)}
      />
      <div className={styles.slug}>
        <WarningIcon />
        <TextField
          className={styles.fullWidth}
          name="slug"
          label="Slug"
          value={slug}
          variant="standard"
          required
          onChange={(e) => setSlug(e.target.value)}
        />
      </div>
      <IconSelect
        value={iconValue}
        onChange={(e: any) => {
          setIconValue(e.target.value);
        }}
      />
      <div className={styles.upload}>
        <UploadImage
          previewUrl={previewUrl || ''}
          fileName={fileName}
          isDeleteBtnShouldBe={false}
          onChange={handleFileChange}
          name="fileInfo"
        />
      </div>
      <div className={styles.tabContainer}>
        <Tabs value={activeTabIndex} onChange={onTabClick}>
          <Tab label={<TabLabel label="Polish" />} />
          <Tab
            label={<TabLabel label="English" />}
          />
          <Tab
            label={<TabLabel label="Russian" />}
          />
          <Tab
            label={<TabLabel label="Ukranian" />}
          />
        </Tabs>
      </div>
      <TextField
        name="title"
        label="Title"
        value={page[title]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [title]: e.target.value,
          }))
        }
      />
      <TextField
        name="subtitle"
        label="Subtitle"
        value={page[subtitle]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [subtitle]: e.target.value,
          }))
        }
      />
      <TextField
        name="description"
        label="Description"
        value={page[description]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [description]: e.target.value,
          }))
        }
      />
      <TextField
        name="h1"
        label="H1"
        value={page[h1]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [h1]: e.target.value,
          }))
        }
      />
      <Typography>SEO for Models.</Typography>
      <Typography>{`A placeholder for inserting the model into the H1, Description, Title fields uses the {{data}}`}</Typography>
      <TextField
        name="modelTitle"
        label="Model Title"
        value={page[modelTitle]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [modelTitle]: e.target.value,
          }))
        }
      />
      <TextField
        name="modelDescription"
        label="Model Description"
        value={page[modelDescription]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [modelDescription]: e.target.value,
          }))
        }
      />
      <TextField
        name="modelH1"
        label="Model H1"
        value={page[modelH1]}
        variant="standard"
        onChange={(e) =>
          setPage((page): any => ({
            ...page,
            [modelH1]: e.target.value,
          }))
        }
      />
      <div className={styles.jsonEditor}>
        <p>FAQ Section</p>
        <JsonEditor
          data={serviceFaq[faqKey]}
          setData={(next) => {
            if (!isServiceHubFaqArray(next)) return;
            setServiceFaq((prev) => ({
              ...prev,
              [faqKey]: next,
            }));
          }}
        />
      </div>
      <DialogActions>
        <Button disabled={loading} variant="outlined" onClick={onCancel}>
          CANCEL
        </Button>
        <LoadingButton
          loading={loading}
          type="submit"
          variant="contained"
          disabled={isSubmitBtnDisabled}
        >
          SAVE
        </LoadingButton>
      </DialogActions>
    </form>
  );
};

const TabLabel = ({ label }: { label: string }) => {
  return (
    <div className={styles.tabItem}>
      <Typography variant="body1">
        {label}
      </Typography>
    </div>
  );
};
